/**
 * The public price sheet: Base, the listed add-ons, extra users and the
 * platform fee.
 *
 * This is the ONE place the public website reads a plan price from while
 * NEXT_PUBLIC_MODULAR_PRICING is on: the pricing page, its FAQ and JSON-LD,
 * `utils/seo.js`, the ROI calculator and the /launch compact card.
 *
 * The server's public `GET /api/pricing-sheet` returns this same shape
 * (MODULAR_SIGNUP_BASE_PLUS_ADDONS_PLAN.md §4.1). /pricing reads it in
 * getStaticProps and falls back to this file on any error or malformed body
 * (`queries/pricingSheet.js`). Everything else imports this file directly.
 *
 * The server's sheet reads its amounts from Stripe (the lookup-key prices
 * signup charges), so a price changed in Stripe shows up on the pricing page,
 * onboarding and the billing page without a deploy. The numbers in THIS file
 * are only the fallback for when that request fails, and what the few static
 * marketing surfaces (SEO text, the ROI calculator) quote.
 *
 * Kitchen Designer is deliberately absent (plan decision P3). Marketing copy
 * (bullets, icons) lives in `addonCopy.js`, keyed by `module`, not here.
 */
// The monthly platform-fee cap (PRICING_SEPT_2026_ROLLOUT_PLAN.md §5.1). The
// server's sheet carries it only while PLATFORM_FEE_CAP_ENABLED is on; this
// fallback (and every static surface that imports it) quotes it only while
// NEXT_PUBLIC_PLATFORM_FEE_CAP is on. Flip both together, server first.
const FALLBACK_FEE_CAP =
  process.env.NEXT_PUBLIC_PLATFORM_FEE_CAP === 'true' ? { amount: 500, period: 'month' } : null;

export const PRICE_SHEET = {
  version: 1,
  currency: 'usd',
  annualMonths: 10, // annual = 10 × monthly ("2 months free")
  platformFeePercent: 1,
  platformFeeCap: FALLBACK_FEE_CAP,
  base: { key: 'base', name: 'TimberCloud', monthly: 199, annual: 1990, seatsIncluded: 3 },
  seat: { monthly: 30, annual: 300 },
  addons: [
    // display order
    // No Website Builder row: it is sold only while the server's
    // WEBSITE_ADDON_ENABLED is on, so only a fetched server sheet may list it
    // (WEBSITE_CMS_ADDON_UNBUNDLING_PLAN.md). A fallback row would advertise it
    // on the public pages whenever the fetch fails, flag on or off.
    { module: 'build-sheets', name: 'Build Sheets & CAM', monthly: 100, annual: 1000, unit: null },
    { module: 'reports', name: 'Advanced Reporting', monthly: 30, annual: 300, unit: null },
    { module: 'purchasing', name: 'Purchasing', monthly: 50, annual: 500, unit: null },
    { module: 'inventory', name: 'Inventory & Warehouses', monthly: 50, annual: 500, unit: 'location' },
    { module: 'shop-floor', name: 'Production / Shop Floor', monthly: 50, annual: 500, unit: null },
    { module: 'b2b-portal', name: 'B2B Dealer Portal', monthly: 100, annual: 1000, unit: null },
  ],
};

const isAmount = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
const hasPrices = (o) => !!o && typeof o === 'object' && isAmount(o.monthly) && isAmount(o.annual);

/**
 * True when `sheet` has every field the website renders. A partial or
 * differently-shaped server response must never reach the page, because a
 * missing number would render as "$NaN" on a crawled pricing page.
 */
export function isValidPriceSheet(sheet) {
  if (!sheet || typeof sheet !== 'object') return false;
  if (!Number.isFinite(sheet.version)) return false;
  if (!isAmount(sheet.platformFeePercent)) return false;
  // Absent (a server from before the cap) or null = no cap is quoted. Every
  // surface words it "a month", so any other period is a malformed sheet.
  const cap = sheet.platformFeeCap;
  if (cap != null && !(isAmount(cap.amount) && cap.amount > 0 && cap.period === 'month')) {
    return false;
  }
  if (!(Number.isFinite(sheet.annualMonths) && sheet.annualMonths > 0)) return false;
  if (!hasPrices(sheet.base) || typeof sheet.base.name !== 'string') return false;
  if (!(Number.isInteger(sheet.base.seatsIncluded) && sheet.base.seatsIncluded >= 0)) return false;
  if (!hasPrices(sheet.seat)) return false;
  if (!Array.isArray(sheet.addons) || sheet.addons.length === 0) return false;
  return sheet.addons.every(
    (a) =>
      hasPrices(a) &&
      typeof a.module === 'string' &&
      a.module.length > 0 &&
      typeof a.name === 'string' &&
      (a.unit === null || a.unit === undefined || typeof a.unit === 'string') &&
      // Inventory's "each additional location" price (the server sends it
      // while INVENTORY_LOCATION_TIERS_ENABLED is on).
      (a.perUnit === null ||
        a.perUnit === undefined ||
        (hasPrices(a.perUnit) && Number.isInteger(a.perUnit.included) && a.perUnit.included >= 0)),
  );
}

/** A valid sheet as given, otherwise the checked-in PRICE_SHEET. */
export function resolvePriceSheet(candidate) {
  const sheet = isValidPriceSheet(candidate) ? candidate : PRICE_SHEET;
  const base = sheet.base.name === 'Base' ? { ...sheet.base, name: 'TimberCloud' } : sheet.base;
  const addons = sheet.addons.map((addon) => addon.module === 'reports' && addon.name === 'Reports' ? { ...addon, name: 'Advanced Reporting' } : addon);
  return base === sheet.base && addons.every((addon, i) => addon === sheet.addons[i]) ? sheet : { ...sheet, base, addons };
}

/** The monthly platform-fee cap in dollars, or null when none is quoted. */
export function platformFeeCapAmount(sheet) {
  const amount = sheet?.platformFeeCap?.amount;
  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0 ? amount : null;
}

/**
 * The one sentence every surface uses for the fee (the review's "we tell you
 * the number"): "1% on card payments taken through TimberCloud, capped at
 * $500 a month". The fee applies to every card payment TimberCloud takes —
 * storefront, invoice links and staff-entered cards alike — never offline
 * payments (D2).
 */
export function platformFeeLine(sheet) {
  const pct = Number(sheet?.platformFeePercent ?? PRICE_SHEET.platformFeePercent);
  const cap = platformFeeCapAmount(sheet);
  return `${pct}% on card payments taken through TimberCloud${cap ? `, capped at ${formatPrice(cap)} a month` : ''}`;
}

/** 'annual' | 'monthly' — anything else reads as monthly. */
export function normalizePeriod(period) {
  return period === 'annual' ? 'annual' : 'monthly';
}

/**
 * An item's price shown "per month" for the chosen billing period. Annual is
 * the yearly price over 12, rounded to cents ($1,990/yr → $165.83/mo). The actual annual charge
 * must appear alongside this comparison figure.
 */
export function monthlyEquivalent(item, period) {
  if (!item) return 0;
  return normalizePeriod(period) === 'annual' ? Math.round(item.annual / 12 * 100) / 100 : item.monthly;
}

/** The add-on row for `module`, or undefined. */
export function findAddon(sheet, module) {
  return (sheet?.addons || []).find((a) => a.module === module);
}

/**
 * How many units of `addon` are billed beyond the ones its module price
 * includes: `units − included` for a tiered add-on (Inventory with location
 * tiers: $50 for the first location, $25 each additional), else 0.
 */
export function addonExtraUnits(addon, units = 1) {
  if (!addon || !addon.perUnit) return 0;
  const n = Math.max(1, Math.floor(Number(units) || 1));
  return Math.max(0, n - (Number(addon.perUnit.included) || 0));
}

/** An add-on's price for `units` of it in a period: module + extras, or per unit. */
export function addonAmount(addon, units = 1, period = 'monthly') {
  const key = normalizePeriod(period);
  if (!addon) return 0;
  if (addon.perUnit) return addon[key] + addon.perUnit[key] * addonExtraUnits(addon, units);
  const n = Math.max(1, Math.floor(Number(units) || 1));
  return addon[key] * (addon.unit === 'location' ? n : 1);
}

/** Lowest and highest add-on price for a period (per unit). */
export function addonPriceRange(sheet, period = 'monthly') {
  const key = normalizePeriod(period);
  const prices = (sheet?.addons || []).map((a) => a[key]);
  if (!prices.length) return { low: 0, high: 0 };
  return { low: Math.min(...prices), high: Math.max(...prices) };
}

/**
 * What a shop pays per billing period: Base + each add-on (per-location
 * add-ons × `locations`) + `extraUsers` × the seat price. Monthly → $/month,
 * annual → $/year. Unknown add-on modules are ignored.
 */
export function quoteShop(sheet, { addons = [], extraUsers = 0, locations = 1 } = {}, period = 'monthly') {
  const key = normalizePeriod(period);
  const units = Math.max(1, Math.floor(Number(locations) || 1));
  const users = Math.max(0, Math.floor(Number(extraUsers) || 0));
  const addonTotal = addons.reduce((sum, module) => {
    const addon = findAddon(sheet, module);
    if (!addon) return sum;
    return sum + addonAmount(addon, units, key);
  }, 0);
  return sheet.base[key] + addonTotal + users * sheet.seat[key];
}

/** Percent saved by paying annually, rounded: 10 of 12 months → 17. */
export function annualSavingsPercent(sheet) {
  const months = Number(sheet?.annualMonths) || 12;
  return Math.round((1 - months / 12) * 100);
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usdCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * USD for display: whole dollars without cents (1990 → "$1,990"), anything else
 * with cents (79.5 → "$79.50"). Prices come from Stripe, so cents are possible.
 */
export function formatPrice(n) {
  const value = Number.isFinite(n) ? n : 0;
  return Number.isInteger(Math.round(value * 100) / 100) ? usd.format(value) : usdCents.format(value);
}

export default PRICE_SHEET;

/** Annual offer copy; the billed amount remains authoritative. */
export function annualOfferLabel(sheet) {
  const freeMonths = Math.max(0, 12 - Number(sheet?.annualMonths || 12));
  return freeMonths > 0 ? `${freeMonths} ${freeMonths === 1 ? 'month' : 'months'} free` : 'Annual billing';
}
